// src/components/treasure/MapTeacherPopups.js
//
// The teacher's popups on the class treasure map:
//   <GoalEditor>   🎯 "Open [50] chests by [Fri]" → setMapGoal
//   <KidCard>      a student's avatar, their chests in this subject, and
//                  🎁 Give coins / 📣 Send a cheer

import React, { useState } from "react";
import ModalPortal from "../common/ModalPortal";
import FocusAvatar from "../avatar/FocusAvatar";
import ChestArt from "../rewards/ChestArt";
import { CHEERS, sendCheer, setMapGoal } from "../../services/classMapService";

const todayNY = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const plusDays = (n) => {
  const d = new Date(`${todayNY()}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const Shell = ({ title, onClose, children, wide }) => (
  <ModalPortal>
    <div className="tt-card-pop" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`tt-card tt-tpop ${wide ? "is-wide" : ""}`}>
        <button type="button" className="tt-tpop-x" onClick={onClose} aria-label="Close">
          ✕
        </button>
        {children}
      </div>
    </div>
  </ModalPortal>
);

export const GoalEditor = ({ subjectKey, goal, classSize = 0, chestsPerKid = 0, opened = 0, onClose }) => {
  const suggestion = Math.max(opened + 1, Math.round(opened + Math.max(5, classSize * 2)));
  const [target, setTarget] = useState(goal && !goal.reached ? Number(goal.target) : suggestion);
  const [by, setBy] = useState(goal && !goal.reached && goal.by ? goal.by : plusDays(7));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const max = classSize * chestsPerKid;

  const save = async (remove = false) => {
    setBusy(true);
    setMsg(null);
    try {
      await setMapGoal(subjectKey, remove ? 0 : Number(target), remove ? "" : by);
      onClose(remove ? "Class goal removed." : "Class goal set! Your students see it on their maps now.");
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Class goal" onClose={() => onClose()}>
      <div className="tt-card-chest">
        <ChestArt tier="gold" />
      </div>
      <p className="tt-card-kicker">🎯 Class goal</p>
      <h2>Open chests together</h2>
      <p className="tt-card-text">
        Every chest a student opens counts. Reach the goal in time and <b>everyone gets a golden gift</b>. Your class has opened <b>{opened}</b>
        {max ? ` of ${max}` : ""} so far.
      </p>
      <div className="tt-goal-form">
        <label>
          Open
          <input type="number" min={opened + 1} max={5000} value={target} onChange={(e) => setTarget(e.target.value)} aria-label="How many chests" />
          chests
        </label>
        <label>
          by
          <input type="date" min={todayNY()} value={by} onChange={(e) => setBy(e.target.value)} aria-label="By when" />
        </label>
      </div>
      {msg && <p className="tt-tpop-msg is-bad">{msg}</p>}
      <div className="tt-card-actions">
        <button type="button" className="tt-btn is-big" disabled={busy || !(Number(target) > opened)} onClick={() => save(false)}>
          {busy ? "Saving…" : "🎯 Set the goal"}
        </button>
        {goal && (
          <button type="button" className="tt-btn is-soft" disabled={busy} onClick={() => save(true)}>
            Remove the goal
          </button>
        )}
      </div>
    </Shell>
  );
};

export const KidCard = ({ kid, topics = [], subjectName = "", onGiveCoins, onClose }) => {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const [custom, setCustom] = useState("");
  if (!kid) return null;
  const done = topics.filter((t) => kid.done?.[t.slug]);
  const next = topics.find((t) => !kid.done?.[t.slug]);
  const at = topics.find((t) => t.slug === kid.spot);

  const cheer = async (text) => {
    if (!text.trim()) return;
    setBusy(text);
    setMsg(null);
    try {
      await sendCheer(kid.uid, text.trim());
      setMsg({ ok: true, text: `Sent! ${kid.name.split(" ")[0]} will see “${text.trim()}” next time they open the site.` });
      setCustom("");
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy("");
    }
  };

  return (
    <Shell title={kid.name} onClose={onClose} wide>
      <div className="tt-kidcard">
        <div className="tt-kidcard-av">
          <FocusAvatar id={`kidcard-${kid.uid}`} uid={kid.uid} size={120} fullSize={200} />
        </div>
        <div className="tt-kidcard-main">
          <p className="tt-card-kicker">{subjectName}</p>
          <h2>{kid.name}</h2>
          <div className="tt-kidcard-stats">
            <span>
              <b>{done.length}</b> of <b>{topics.length}</b> chests opened
            </span>
            {at && <span>📍 at “{at.title}”</span>}
            {next ? <span>➡️ next: “{next.title}”</span> : <span>🏆 opened every chest!</span>}
          </div>
          <div className="tt-kidcard-track">
            <span style={{ width: `${topics.length ? Math.round((done.length / topics.length) * 100) : 0}%` }} />
          </div>
          {done.length > 0 && (
            <div className="tt-card-kids">
              {done.map((t) => (
                <span key={t.slug} className="tt-card-kid is-done">
                  <span className="tt-card-kid-chest">
                    <ChestArt tier="gold" open />
                  </span>
                  {t.title}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="tt-kidcard-actions">
        <button type="button" className="sp-give-btn" onClick={() => onGiveCoins?.(kid.uid)}>
          🎁 Give coins
        </button>
      </div>

      <p className="tt-card-kicker" style={{ marginTop: 12 }}>
        📣 Send a cheer — it pops up for them
      </p>
      <div className="tt-cheers">
        {CHEERS.map((c) => (
          <button key={c} type="button" className="tt-cheer" disabled={Boolean(busy)} onClick={() => cheer(c)}>
            {busy === c ? "Sending…" : c}
          </button>
        ))}
      </div>
      <form
        className="tt-cheer-own"
        onSubmit={(e) => {
          e.preventDefault();
          cheer(custom);
        }}
      >
        <input value={custom} maxLength={80} onChange={(e) => setCustom(e.target.value)} placeholder="…or write your own" aria-label="Your own cheer" />
        <button type="submit" className="tt-btn" disabled={Boolean(busy) || !custom.trim()}>
          Send
        </button>
      </form>
      {msg && <p className={`tt-tpop-msg ${msg.ok ? "is-ok" : "is-bad"}`}>{msg.text}</p>}
    </Shell>
  );
};
